import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';

import * as api from '../../utils/api';
import { fixerPhotoUrl } from '../../utils/fixers';
import useRequireLogin from '../../hooks/useRequireLogin';

import Container from '../../components/shared/atoms/Container';
import Row from '../../components/shared/atoms/Row';
import Box from '../../components/shared/atoms/Box';
import Heading from '../../components/shared/atoms/Heading';
import Text from '../../components/shared/atoms/Text';
import Button from '../../components/shared/atoms/Button';
import Badge from '../../components/shared/atoms/Badge';
import EmptyState from '../../components/shared/molecules/EmptyState';
import AccountNav from '../../components/customerArea/shared/AccountNav';
import AppPromoCard from '../../components/shared/organisms/AppPromoCard';
import Check16 from '../../components/icons/Check16';

const PAGE_SIZE = 10;
const ALL = '__all__';

const FixerPhoto = styled('img')`
  width: 24px;
  height: 24px;
  border-radius: 100%;
  margin-right: 8px;
  object-fit: cover;
`;

const CHEVRON_DATA_URL =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%232E414B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>\")";

const NativeSelect = styled.select`
  appearance: none;
  width: 100%;
  padding: 10px 32px 10px 12px;
  font-size: 14px;
  font-family: inherit;
  color: ${(p) => p.theme.colors.black};
  background: ${(p) => p.theme.colors.white} ${CHEVRON_DATA_URL} no-repeat right
    12px center;
  border: 1px solid ${(p) => p.theme.colors.gray.main};
  border-radius: ${(p) => p.theme.radii.xs};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.colors.blue.electric};
  }
`;

const HistoriqueSkeleton = () => (
  <Box data-testid="historique-skeleton">
    {[0, 1, 2].map((i) => (
      <Box key={i} mb={4}>
        <Skeleton style={{ borderRadius: 8 }} height={120} />
      </Box>
    ))}
  </Box>
);

const formatDate = (iso, lang) => {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(lang || 'fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
};

const yearOf = (iso) => {
  if (!iso) return null;
  const d = new Date(iso.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d.getFullYear();
};

const HistoriqueRow = ({ course }) => {
  const { t, i18n } = useTranslation();
  const report = course.visit_report;
  const reportPublished = !!(report && report.published_at);

  return (
    <Box
      as="article"
      borderColor="gray.light"
      borderWidth="1px"
      borderRadius="sm"
      borderStyle="solid"
      p={{ xs: 3, lg: 4 }}
      mb={4}
      data-testid="historique-row">
      <Row md gap={16} alignItems="center">
        <Box style={{ flex: 4 }}>
          <Text fontWeight={2} color="black" mb={1}>
            {formatDate(course.date, i18n.language)}
          </Text>
          {course.fixer && (
            <Box flexDirection="row" alignItems="center" mb={1}>
              <FixerPhoto
                src={fixerPhotoUrl(course.fixer)}
                alt={t('historique.fixer.alt', {
                  defaultValue: 'Photo du réparateur',
                })}
              />
              <Text>{course.fixer.prenom}</Text>
            </Box>
          )}
          {course.bike_details && (
            <Text color="gray.dark" fontSize="sm">
              {course.bike_details}
            </Text>
          )}
          {reportPublished && (
            <Box mt={2}>
              <Badge before={<Check16 />} color="green" light>
                {t('historique.report.available')}
              </Badge>
            </Box>
          )}
        </Box>
        <Box style={{ flex: 3 }} mt={{ xs: 3, lg: 0 }}>
          {reportPublished && (
            <Button
              fullWidth
              variant="filled"
              to={`/rapport/${report.token}`}
              mb={2}
              data-testid="historique-row-report">
              {t('historique.report.cta')}
            </Button>
          )}
          {course.invoice_url && (
            <Button
              fullWidth
              variant="outlinedBlack"
              href={course.invoice_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="historique-row-invoice">
              {t('historique.invoice.cta')}
            </Button>
          )}
        </Box>
      </Row>
    </Box>
  );
};

const Historique = () => {
  const { t } = useTranslation();
  const client = useSelector((s) => s.client);
  useRequireLogin();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bikeFilter, setBikeFilter] = useState(ALL);
  const [yearFilter, setYearFilter] = useState(ALL);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    if (!client.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await api.get('/courses');
      if (cancelled) return;
      const list = Array.isArray(res)
        ? res.filter((c) => c.status === 'TERMINE')
        : [];
      list.sort((a, b) => (a.date < b.date ? 1 : -1));
      setCourses(list);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  const bikeOptions = useMemo(() => {
    const set = new Map();
    courses.forEach((c) => {
      if (c.bike_details && !set.has(c.bike_details)) {
        set.set(c.bike_details, c.bike_details);
      }
    });
    return Array.from(set.keys());
  }, [courses]);

  const yearOptions = useMemo(() => {
    const years = new Set();
    courses.forEach((c) => {
      const y = yearOf(c.date);
      if (y) years.add(y);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [courses]);

  const filtered = useMemo(() => {
    return courses.filter((c) => {
      if (bikeFilter !== ALL && c.bike_details !== bikeFilter) return false;
      if (yearFilter !== ALL && yearOf(c.date) !== Number(yearFilter))
        return false;
      return true;
    });
  }, [courses, bikeFilter, yearFilter]);

  // Reset pagination when filters change.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [bikeFilter, yearFilter]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  return (
    <Container>
      <Helmet
        title={t('historique.meta.title')}
        meta={[{ name: 'robots', content: 'noindex, nofollow' }]}
      />
      <Container fluid pb={10} pt={4}>
        <Row md gap={56}>
          <AccountNav />
          <Box mt={{ xs: -7, lg: 0 }} style={{ flex: 1 }}>
            <Heading level={2} mb={{ xs: 4, lg: 6 }}>
              {t('historique.title')}
            </Heading>

            {!loading && courses.length > 0 && (
              <Row md gap={16} mb={5}>
                <Box style={{ flex: 1 }}>
                  <Text as="label" htmlFor="historique-filter-bike" mb={1}>
                    {t('historique.filter.bike')}
                  </Text>
                  <NativeSelect
                    id="historique-filter-bike"
                    data-testid="historique-filter-bike"
                    value={bikeFilter}
                    onChange={(e) => setBikeFilter(e.target.value)}
                    aria-label={t('historique.filter.bike')}>
                    <option value={ALL}>{t('historique.filter.all')}</option>
                    {bikeOptions.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </NativeSelect>
                </Box>
                <Box style={{ flex: 1 }} mt={{ xs: 3, md: 0 }}>
                  <Text as="label" htmlFor="historique-filter-year" mb={1}>
                    {t('historique.filter.year')}
                  </Text>
                  <NativeSelect
                    id="historique-filter-year"
                    data-testid="historique-filter-year"
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    aria-label={t('historique.filter.year')}>
                    <option value={ALL}>{t('historique.filter.all')}</option>
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </NativeSelect>
                </Box>
              </Row>
            )}

            {loading ? (
              <HistoriqueSkeleton />
            ) : filtered.length === 0 ? (
              <Box alignItems="center">
                <EmptyState
                  width="60%"
                  title={t('historique.empty.title')}
                  subTitle={t('historique.empty.subtitle')}
                />
              </Box>
            ) : (
              <Box>
                {visible.map((c) => (
                  <HistoriqueRow key={c.id} course={c} />
                ))}
                {hasMore && (
                  <Box alignItems="center" mt={4}>
                    <Button
                      variant="outlinedBlack"
                      onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                      data-testid="historique-loadmore">
                      {t('historique.loadmore')}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
            {!loading && <AppPromoCard placement="histo" mt={6} />}
          </Box>
        </Row>
      </Container>
    </Container>
  );
};

export default Historique;
